/**
 * Settings › Users — sibling workspaces only (ACT2-1124). The members and invites, each member's RudderStack role,
 * the organization members who are not in Lookout, and each member's permissions. Any other workspace keeps its
 * members and invites on Settings › General, so this page sends it there and reads no sibling data.
 */
import { useQuery } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { Callout } from '@/_shared/components/ui/callout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useIsSiblingWorkspace } from '@/_shared/hooks/useSiblingStatus';

import { SiblingMembers } from '../components/members/SiblingMembers';

function Loading(): React.ReactElement {
  return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <LoaderCircle size={24} className="animate-spin" />
    </div>
  );
}

/** The people of a sibling workspace: its Lookout members, and the RudderStack organization's view of them. */
function SiblingUsers(): React.ReactElement {
  const orpc = useOrpc();
  const { wp } = useWorkspace();
  // The row actions need the signed-in person: nobody may remove themselves or change their own role.
  const { loading: authLoading } = useAuth();
  const { data, isLoading } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));
  const overviewQuery = useQuery(orpc.siblingMembers.overview.queryOptions({ input: {} }));
  const members = useMemo(() => data?.members ?? [], [data]);

  if (authLoading || isLoading || overviewQuery.isLoading) return <Loading />;
  if (overviewQuery.isError) {
    return (
      <Callout variant="error" role="alert">
        Failed to load members: {overviewQuery.error.message}
      </Callout>
    );
  }
  const overview = overviewQuery.data;
  if (overview?.model !== 'sibling') return <Navigate to={wp('/settings')} replace />;
  return <SiblingMembers members={members} overview={overview} />;
}

export function SettingsUsers(): React.ReactElement {
  usePageTitle('Users — Settings');
  const { wp } = useWorkspace();
  const isSiblingWorkspace = useIsSiblingWorkspace();
  if (isSiblingWorkspace === null) return <Loading />;
  if (!isSiblingWorkspace) return <Navigate to={wp('/settings')} replace />;
  return <SiblingUsers />;
}
